/**
 * The saved-workout library — the IndexedDB {@link PlanStorePort}.
 *
 * Three rules from F-BD-05 drive everything in this file, and each of them is a
 * *re-read*, never a return value the caller is asked to trust:
 *
 *  - `save` succeeds only when the plan read back out of storage encodes to the
 *    same canonical text as the plan that was handed in.
 *  - `reorder` succeeds only when the id order observed after a refresh is the
 *    order that was requested.
 *  - `delete` succeeds only when the plan is absent after a refresh.
 *
 * The kernel already verifies each row inside its own transaction. The checks
 * here are deliberately *outside* it and phrased in the terms the spec uses —
 * the plan, and the order of the library — because the row is the thing the
 * store wrote and the plan is the thing the user saved. A row that verified
 * while the library it belongs to came back in a different order is still a
 * failure the user has to be told about.
 *
 * **Order is persisted, because the user arranged it.** A record store has no
 * inherent order, so each row carries an explicit `position` beside its plan.
 * Holding the order in memory — the way the domain's `FakePlanStore` does,
 * where nothing survives the process — would silently reset the library on
 * every reload, and a library that reorders itself is indistinguishable from
 * one that lost the arrangement.
 */

import type {
  SchemaVersion,
  StoreReadResult,
  WorkoutMode,
  WorkoutPlan,
  WorkoutPlanId,
  WriteOutcome,
} from "@ff/domain";
import { STORE_IDS, schemaVersion, validateWorkoutPlan, verifyWrite } from "@ff/domain";
import type {
  KeyedRecord,
  ObservedMutationGate,
  PlanReorderOutcome,
  PlanStorePort,
  RecordMutation,
  RecordStorePort,
} from "@ff/domain/ports";

import type { PayloadDecode, RecordDatabase, RecordIndexFields } from "../kernel";
import { canonicalJson, createRecordStore } from "../kernel";

/**
 * The version this build stamps on a saved-plan envelope.
 *
 * `savedPlans` has `currentSchemaVersion: null` in `STORE_DESCRIPTORS` because
 * it versions its envelope rather than its payload, so the number has to live
 * with the code that writes it. Anything above it is refused by the codec and
 * therefore freezes every mutation on the store — which is the whole point of
 * naming it here rather than leaving each write to pick its own.
 */
export const SAVED_PLAN_ENVELOPE_VERSION: SchemaVersion = schemaVersion(1);

/**
 * One row in the library: a plan and where the user put it.
 *
 * `position` is not an index into the read result — it is a sort key that
 * survives deletion. Renumbering every surviving row after each delete would
 * turn a one-row mutation into a whole-library rewrite, and a rewrite that
 * fails halfway is exactly the partial state the verified-write path exists to
 * make unreachable.
 */
export interface SavedPlanRecord {
  readonly position: number;
  readonly plan: WorkoutPlan;
}

const WORKOUT_MODES: readonly WorkoutMode[] = ["simple", "guided"];

/**
 * Structural validation for one stored row.
 *
 * It checks the fields *this store* addresses — the sort key and the plan
 * identity, name and mode it orders and indexes by — and stops there. Running
 * `validateWorkoutPlan` on decode would be worse than useless: the authored
 * dosage bounds are product policy, and a build that tightened a range would
 * make every previously legal saved workout unreadable, turning a policy change
 * into data loss. Authoring invariants are enforced on the way *in* (see
 * {@link IndexedDbPlanStore.save}), where refusing costs the user nothing.
 *
 * The refusal reports `corruptBytes` rather than `unsupportedShape` because
 * `savedPlans` declares exactly three triggers — `futureSchema`,
 * `duplicateIdentity`, `corruptBytes` — and a store that raised a trigger its
 * descriptor does not list would produce recovery copy nobody wrote.
 */
function decodeSavedPlanRecord(payload: unknown): PayloadDecode<SavedPlanRecord> {
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
    return { ok: false, trigger: "corruptBytes", detail: "saved plan row is not an object" };
  }
  const row = payload as Record<string, unknown>;

  const position = row["position"];
  if (typeof position !== "number" || !Number.isInteger(position)) {
    return {
      ok: false,
      trigger: "corruptBytes",
      detail: "saved plan row has no integer position",
    };
  }

  const plan = row["plan"];
  if (plan === null || typeof plan !== "object" || Array.isArray(plan)) {
    return { ok: false, trigger: "corruptBytes", detail: "saved plan row has no plan" };
  }
  const fields = plan as Record<string, unknown>;

  if (typeof fields["id"] !== "string" || fields["id"].length === 0) {
    return { ok: false, trigger: "corruptBytes", detail: "saved plan has no id" };
  }
  if (typeof fields["name"] !== "string") {
    return { ok: false, trigger: "corruptBytes", detail: "saved plan has no name" };
  }
  const mode = fields["mode"];
  if (typeof mode !== "string" || !WORKOUT_MODES.includes(mode as WorkoutMode)) {
    return { ok: false, trigger: "corruptBytes", detail: `saved plan mode "${String(mode)}"` };
  }
  if (!Array.isArray(fields["sections"])) {
    return { ok: false, trigger: "corruptBytes", detail: "saved plan has no sections" };
  }

  // Everything the store itself relies on has now been checked. The remaining
  // authoring invariants were proven by `validateWorkoutPlan` before this row
  // was ever written, so re-asserting the type here restores what the decode
  // erased rather than widening anything.
  return { ok: true, value: { position, plan: plan as WorkoutPlan } };
}

/** The materialized scalars the layout declares for `savedPlans`. */
function indexSavedPlan(record: SavedPlanRecord): RecordIndexFields {
  return { name: record.plan.name, mode: record.plan.mode };
}

/** The `savedPlans` record store, configured with this store's codec and index. */
export function createPlanRecordStore(
  database: RecordDatabase,
): RecordStorePort<SavedPlanRecord> {
  return createRecordStore<SavedPlanRecord>({
    store: STORE_IDS.savedPlans,
    schemaVersion: SAVED_PLAN_ENVELOPE_VERSION,
    database,
    decodePayload: decodeSavedPlanRecord,
    index: indexSavedPlan,
    // Two rows claiming one `WorkoutPlanId` make "the plan this row refers to"
    // unanswerable, so the library refuses to be read rather than choosing
    // between them. The identity is the id *inside the payload*: a row stored
    // under one key whose plan claims another id is exactly the collision a
    // key-only check would miss.
    identity: (record) => record.value.plan.id,
  });
}

/**
 * The library's rules over any record store that holds saved-plan rows.
 *
 * Split from {@link createPlanRecordStore} because ordering, verification and
 * permutation checking have nothing to do with where the bytes live — and
 * because the interesting failures (a re-read that disagrees with what was
 * written) are reachable only by substituting the records underneath.
 */
export function planLibrary(records: RecordStorePort<SavedPlanRecord>): PlanStorePort {
  return new IndexedDbPlanStore(records);
}

export function createPlanStore(database: RecordDatabase): PlanStorePort {
  return planLibrary(createPlanRecordStore(database));
}

class IndexedDbPlanStore implements PlanStorePort {
  readonly #records: RecordStorePort<SavedPlanRecord>;

  constructor(records: RecordStorePort<SavedPlanRecord>) {
    this.#records = records;
  }

  async read(): Promise<StoreReadResult<readonly WorkoutPlan[]>> {
    return mapLoaded(await this.#records.read(), (records) =>
      [...records].sort(byPosition).map((record) => record.value.plan),
    );
  }

  async readPlan(id: WorkoutPlanId): Promise<StoreReadResult<WorkoutPlan>> {
    return mapLoaded(await this.#records.readRecord(id), (record) => record.plan);
  }

  mutationGate(): ObservedMutationGate {
    return this.#records.mutationGate();
  }

  async save(plan: WorkoutPlan): Promise<WriteOutcome> {
    // A plan with no flattened segments would archive as a session that never
    // happened, and duplicate section or interval ids cannot be addressed at
    // runtime (F-BD-05). Refusing here is refusing before any byte moves.
    if (!validateWorkoutPlan(plan).isValid) {
      return { status: "refused", reason: "unsupportedShape" };
    }

    const current = await this.#records.read();
    if (current.status === "unreadable") {
      return { status: "refused", reason: current.trigger };
    }
    const records = current.status === "loaded" ? current.value : [];

    // Editing reuses the id, so an edit keeps the position the user put the
    // plan in. Re-appending an edited plan would move it to the end of the
    // library every time it was saved.
    const existing = records.find((record) => record.key === plan.id);
    const position = existing?.value.position ?? nextPosition(records);

    const outcome = await this.#records.commit([
      { kind: "put", key: plan.id, value: { position, plan } },
    ]);
    if (outcome.status !== "verified") return outcome;

    // F-BD-05 states the success condition over the *plan*, so it is checked
    // over the plan: the row verified inside the transaction, and this proves
    // the thing the user saved reads back as the thing the user saved.
    const expected = canonicalJson(plan);
    const reRead = await this.#records.readRecord(plan.id);
    if (reRead.status !== "loaded") {
      return { status: "mismatch", expectedCanonical: expected, actualCanonical: null };
    }
    return verifyWrite(expected, canonicalJson(reRead.value.plan));
  }

  async delete(id: WorkoutPlanId): Promise<WriteOutcome> {
    const outcome = await this.#records.commit([{ kind: "remove", key: id }]);
    if (outcome.status !== "verified") return outcome;

    const reRead = await this.#records.readRecord(id);
    if (reRead.status === "missing") return { status: "verified" };
    if (reRead.status === "unreadable") {
      return { status: "refused", reason: reRead.trigger };
    }
    // Absence has a canonical form — the empty string — because a delete is
    // verified exactly as a write is, and "still there" has to be reportable as
    // a mismatch rather than as a success the caller believes in.
    return {
      status: "mismatch",
      expectedCanonical: "",
      actualCanonical: canonicalJson(reRead.value.plan),
    };
  }

  async reorder(order: readonly WorkoutPlanId[]): Promise<PlanReorderOutcome> {
    const current = await this.#records.read();
    if (current.status === "unreadable") {
      return { kind: "unavailable", reason: current.trigger };
    }
    const records = current.status === "loaded" ? current.value : [];

    // `rejected` is a programming error and `notConfirmed` is a storage
    // failure. They carry different copy — one of them tells the user their
    // previous order was kept — so a request that is not a permutation is
    // refused here rather than attempted and reported as a failed write.
    const stored = new Set(records.map((record) => record.key));
    const requested = new Set<string>(order);
    if (requested.size !== order.length || requested.size !== stored.size) {
      return { kind: "rejected", reason: "notAPermutation" };
    }
    for (const id of order) {
      if (!stored.has(id)) return { kind: "rejected", reason: "notAPermutation" };
    }

    const byKey = new Map(records.map((record) => [record.key, record.value]));
    const mutations: RecordMutation<SavedPlanRecord>[] = [];
    order.forEach((id, position) => {
      const record = byKey.get(id);
      // A row already sitting at its target position is not rewritten. Every
      // position this store ever assigns is unique, so "already correct" cannot
      // be true of two rows at once.
      if (record === undefined || record.position === position) return;
      mutations.push({ kind: "put", key: id, value: { position, plan: record.plan } });
    });

    const outcome = await this.#records.commit(mutations);
    if (outcome.status === "refused") {
      return { kind: "unavailable", reason: outcome.reason };
    }
    if (outcome.status !== "verified") return { kind: "notConfirmed" };

    const refreshed = await this.read();
    if (refreshed.status !== "loaded") return { kind: "notConfirmed" };
    const observed = refreshed.value.map((plan) => plan.id);
    if (observed.length !== order.length) return { kind: "notConfirmed" };
    for (let index = 0; index < order.length; index += 1) {
      if (observed[index] !== order[index]) return { kind: "notConfirmed" };
    }
    return { kind: "reordered" };
  }
}

/* --------------------------------------------------------------------------- *
 * Ordering
 * --------------------------------------------------------------------------- */

/**
 * Position first, then the record key. The tie-break can only matter for rows
 * written by a build that is not this one, and a library whose order depended
 * on IndexedDB's iteration order would be a library that reshuffled itself for
 * reasons nobody could reproduce.
 */
function byPosition(
  left: KeyedRecord<SavedPlanRecord>,
  right: KeyedRecord<SavedPlanRecord>,
): number {
  if (left.value.position !== right.value.position) {
    return left.value.position - right.value.position;
  }
  if (left.key === right.key) return 0;
  return left.key < right.key ? -1 : 1;
}

function nextPosition(records: readonly KeyedRecord<SavedPlanRecord>[]): number {
  let highest = -1;
  for (const record of records) highest = Math.max(highest, record.value.position);
  return highest + 1;
}

/**
 * Transforms the `loaded` case and passes the other two through untouched.
 *
 * `missing` and `unreadable` mention no payload type, so they are already
 * results of the target type — which is the point: there is no place here where
 * an unreadable library could be turned into an empty one.
 */
function mapLoaded<A, B>(
  result: StoreReadResult<A>,
  transform: (value: A) => B,
): StoreReadResult<B> {
  if (result.status !== "loaded") return result;
  return { ...result, value: transform(result.value) };
}
